
interface SkillsShowcaseProps {
  activeVideo?: string | null;
  setActiveVideo?: (video: string | null) => void;
}

export default function SkillsShowcase({ }: SkillsShowcaseProps) {
  const skills = [
    { name: 'Passing Accuracy', level: 92, icon: 'ri-focus-3-fill', color: 'from-orange-500 to-red-500' },
    { name: 'Defensive Digs', level: 88, icon: 'ri-shield-star-fill', color: 'from-blue-500 to-cyan-500' },
    { name: 'Court Coverage', level: 95, icon: 'ri-run-fill', color: 'from-green-500 to-emerald-500' },
    { name: 'Serve Receive', level: 90, icon: 'ri-hand-coin-fill', color: 'from-purple-500 to-pink-500' },
    { name: 'Communication', level: 94, icon: 'ri-chat-voice-fill', color: 'from-yellow-500 to-orange-500' },
    { name: 'Game IQ', level: 87, icon: 'ri-lightbulb-flash-fill', color: 'from-red-500 to-rose-500' },
  ];

  const attributes = [
    { label: 'Speed', value: 'Elite', icon: 'ri-flashlight-fill' },
    { label: 'Agility', value: 'Elite', icon: 'ri-tornado-fill' },
    { label: 'Reaction Time', value: '0.18s', icon: 'ri-timer-flash-fill' },
    { label: 'Vertical', value: '24"', icon: 'ri-arrow-up-double-fill' },
  ];

  return (
    <section className="py-20 px-4 bg-gradient-to-br from-gray-900 to-gray-800 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full opacity-10">
        <div className="absolute top-20 left-20 w-64 h-64 bg-orange-500 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-64 h-64 bg-red-500 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-purple-500 rounded-full blur-3xl opacity-50"></div>
      </div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <div className="inline-block mb-4 px-4 py-1 bg-orange-500/20 border border-orange-500/30 rounded-full">
            <span className="text-orange-400 font-semibold text-sm tracking-wider">LIBERO SPECIALIST</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-black text-white mb-4">
            SKILLS & STRENGTHS
          </h2>
          <div className="w-24 h-2 bg-gradient-to-r from-orange-500 to-red-500 mx-auto rounded-full mb-4"></div>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Dedicated to defensive excellence and floor leadership
          </p>
        </div>

        {/* Athletic Attributes */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {attributes.map((attr, index) => (
            <div 
              key={index}
              className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-6 text-center hover:border-orange-500/50 transition-all duration-300 hover:scale-105 group"
            >
              <div className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <i className={`${attr.icon} text-2xl text-white`}></i>
              </div>
              <div className="text-2xl font-black text-white mb-1">{attr.value}</div>
              <div className="text-gray-400 text-sm font-medium">{attr.label}</div>
            </div>
          ))}
        </div>
        
        {/* Skills Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {skills.map((skill, index) => (
            <div 
              key={index}
              className="group bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-700 hover:border-orange-500/50 transition-all duration-300 hover:scale-105"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-12 h-12 bg-gradient-to-br ${skill.color} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                  <i className={`${skill.icon} text-xl text-white`}></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">{skill.name}</h3>
                  <span className="text-2xl font-black text-orange-400">{skill.level}%</span>
                </div>
              </div>
              <div className="h-3 bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full bg-gradient-to-r ${skill.color} rounded-full transition-all duration-1000`}
                  style={{ width: `${skill.level}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>

        {/* Quote */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-2xl p-8 max-w-3xl">
            <i className="ri-double-quotes-l text-4xl text-orange-500 mb-4 block"></i>
            <p className="text-xl md:text-2xl text-white font-medium italic mb-4">
              "Defense wins championships. Every dig, every save, every hustle play matters."
            </p>
            <p className="text-orange-400 font-bold">— Jazzlyn Vassilatos</p>
          </div>
        </div>
      </div>
    </section>
  );
}
